import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Download, LogOut, RefreshCw, Search, Users, Inbox } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'cwr_admin_token';

const SOURCE_LABELS = {
  contact: 'Contact',
  'free-resources': 'Free Resources',
  consultation: 'Consultation',
  roadmap: 'Roadmap',
  'instagram-landing': 'Instagram',
};

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [leads, setLeads] = useState(null);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState('leads');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  const fetchLeads = async (t = token) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t}` } });
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setLeads(data);
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken('');
      setLeads(null);
      toast.error('Session expired. Please sign in again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async (t = token) => {
    try {
      const { data } = await axios.get(`${API}/payments`, { headers: { Authorization: `Bearer ${t}` } });
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setPayments(data);
    } catch {
      // auth failures are surfaced by fetchLeads
    }
  };

  const fetchAll = (t = token) => {
    fetchLeads(t);
    fetchPayments(t);
  };

  useEffect(() => {
    if (token) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success('Welcome back.');
      fetchAll(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setLeads(null);
    setEmail('');
    setPassword('');
  };

  const filtered = useMemo(() => {
    if (!leads) return [];
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter !== 'All' && l.source !== filter) return false;
      if (!q) return true;
      return [l.name, l.email, l.phone, l.interest, l.education].some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [leads, filter, search]);

  const downloadCSV = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
    const rows = [['Date', 'Name', 'Email', 'Phone', 'Education', 'Interest', 'Message', 'Source'].map(esc).join(',')];
    filtered.forEach((l) =>
      rows.push(
        [new Date(l.created_at).toLocaleString(), l.name, l.email, l.phone, l.education, l.interest, l.message || '', SOURCE_LABELS[l.source] || l.source]
          .map(esc)
          .join(',')
      )
    );
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `careerwithraaj-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token || !leads) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-navy px-6 pt-20" data-testid="admin-login-page">
        <SEO title="Admin | CareerWithRaaj" description="Private admin area." />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md rounded-3xl border border-white/10 bg-navy-light p-8 shadow-card-hover md:p-10"
        >
          <div className="flex items-center justify-between">
            <img src="/logo-wordmark.png" alt="CareerWithRaaj" className="h-9 w-auto rounded-lg bg-white px-2.5 py-1.5" />
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-coral/15 text-coral">
              <Lock className="h-5 w-5" />
            </span>
          </div>
          <h1 className="mt-6 font-display text-2xl font-bold text-white">Leads Dashboard</h1>
          <p className="mt-2 text-sm text-white/60">Private area. Sign in to view and download your leads.</p>
          <form onSubmit={login} className="mt-8 space-y-4" data-testid="admin-login-form">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin Email"
              data-testid="admin-email-input"
              aria-label="Admin email"
              className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-white/40 transition-colors focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
            />
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="admin-password-input"
              aria-label="Admin password"
              className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-white/40 transition-colors focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full rounded-full bg-coral py-4 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-coral-deep disabled:opacity-60"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </motion.div>
      </main>
    );
  }

  const sources = ['All', ...new Set(leads.map((l) => l.source))];

  return (
    <main className="min-h-screen bg-mist pb-24 pt-28" data-testid="admin-dashboard">
      <SEO title="Leads Dashboard | CareerWithRaaj" description="Private admin area." />
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-coral">Admin</p>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Leads Dashboard</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchAll()}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-navy/40"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              onClick={downloadCSV}
              data-testid="admin-download-csv-button"
              className="flex items-center gap-2 rounded-full bg-coral px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-coral-deep"
            >
              <Download className="h-4 w-4" /> Download CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-navy/40"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-line bg-white p-6 shadow-card" data-testid="admin-stat-total">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-coral/10 text-coral">
                <Users className="h-5 w-5" />
              </span>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Leads</p>
            </div>
            <p className="mt-3 font-display text-3xl font-extrabold text-navy">{leads.length}</p>
          </div>
          {Object.entries(SOURCE_LABELS)
            .filter(([key]) => leads.some((l) => l.source === key))
            .slice(0, 3)
            .map(([key, label]) => (
              <div key={key} className="rounded-2xl border border-line bg-white p-6 shadow-card" data-testid={`admin-stat-${key}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Inbox className="h-5 w-5" />
                  </span>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
                </div>
                <p className="mt-3 font-display text-3xl font-extrabold text-navy">
                  {leads.filter((l) => l.source === key).length}
                </p>
              </div>
            ))}
        </div>

        <div className="mt-8 flex gap-2" data-testid="admin-tabs">
          <button
            onClick={() => setTab('leads')}
            data-testid="admin-tab-leads"
            className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === 'leads' ? 'border-coral bg-coral text-white' : 'border-line bg-white text-navy/70 hover:border-navy/40'
            }`}
          >
            Leads ({leads.length})
          </button>
          <button
            onClick={() => setTab('payments')}
            data-testid="admin-tab-payments"
            className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === 'payments' ? 'border-coral bg-coral text-white' : 'border-line bg-white text-navy/70 hover:border-navy/40'
            }`}
          >
            Payments ({payments.filter((p) => p.status === 'paid').length} paid)
          </button>
        </div>

        {tab === 'leads' && (
          <>
        <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
            {sources.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`admin-filter-${s.toLowerCase()}`}
                className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                  filter === s ? 'border-coral bg-coral text-white' : 'border-line bg-white text-navy/70 hover:border-navy/40'
                }`}
              >
                {s === 'All' ? 'All' : SOURCE_LABELS[s] || s}
              </button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, interest…"
              data-testid="admin-search-input"
              aria-label="Search leads"
              className="w-full rounded-full border border-line bg-white py-2.5 pl-10 pr-4 text-sm text-ink placeholder:text-slate-400 transition-colors focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
            />
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-3xl border border-line bg-white shadow-card" data-testid="admin-leads-table">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-4 font-bold">Date</th>
                <th className="px-5 py-4 font-bold">Name</th>
                <th className="px-5 py-4 font-bold">Email</th>
                <th className="px-5 py-4 font-bold">Phone</th>
                <th className="px-5 py-4 font-bold">Education</th>
                <th className="px-5 py-4 font-bold">Interest</th>
                <th className="px-5 py-4 font-bold">Message</th>
                <th className="px-5 py-4 font-bold">Source</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-slate-400" data-testid="admin-empty-state">
                    No leads match this view yet.
                  </td>
                </tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-line/60 transition-colors last:border-0 hover:bg-mist" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-5 py-4 font-semibold text-navy">{l.name}</td>
                  <td className="px-5 py-4 text-slate-600">{l.email}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-600">{l.phone}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-600">{l.education}</td>
                  <td className="px-5 py-4 text-slate-600">{l.interest}</td>
                  <td className="max-w-[220px] truncate px-5 py-4 text-slate-500" title={l.message || ''}>{l.message || '—'}</td>
                  <td className="px-5 py-4">
                    <span className="whitespace-nowrap rounded-full bg-coral/10 px-3 py-1 text-xs font-semibold text-coral">
                      {SOURCE_LABELS[l.source] || l.source}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-slate-400">
          Showing {filtered.length} of {leads.length} leads. CSV download exports the current filtered view.
        </p>
          </>
        )}

        {tab === 'payments' && (
          <>
            <div className="mt-6 overflow-x-auto rounded-3xl border border-line bg-white shadow-card" data-testid="admin-payments-table">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead>
                  <tr className="border-b border-line text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-4 font-bold">Date</th>
                    <th className="px-5 py-4 font-bold">Plan</th>
                    <th className="px-5 py-4 font-bold">Amount</th>
                    <th className="px-5 py-4 font-bold">Status</th>
                    <th className="px-5 py-4 font-bold">Payment ID</th>
                    <th className="px-5 py-4 font-bold">Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-12 text-center text-slate-400" data-testid="admin-payments-empty">
                        No payments yet.
                      </td>
                    </tr>
                  )}
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-line/60 transition-colors last:border-0 hover:bg-mist" data-testid={`admin-payment-row-${p.id}`}>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-500">{new Date(p.created_at).toLocaleString()}</td>
                      <td className="whitespace-nowrap px-5 py-4 font-semibold text-navy">{p.plan_name}</td>
                      <td className="whitespace-nowrap px-5 py-4 font-semibold text-navy">₹{(p.amount / 100).toLocaleString('en-IN')}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
                            p.status === 'paid'
                              ? 'bg-emerald-100 text-emerald-700'
                              : p.status === 'created'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                          }`}
                          data-testid={`payment-status-${p.id}`}
                        >
                          {p.status === 'paid' ? 'Paid' : p.status === 'created' ? 'Abandoned' : 'Failed'}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-mono text-xs text-slate-500">{p.payment_id || '—'}</td>
                      <td className="px-5 py-4 font-mono text-xs text-slate-500">{p.order_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-slate-400" data-testid="admin-payments-summary">
              Total collected: ₹
              {(payments.filter((p) => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0) / 100).toLocaleString('en-IN')}{' '}
              · "Abandoned" means the visitor opened checkout but didn't complete payment.
            </p>
          </>
        )}
      </div>
    </main>
  );
};

export default Admin;
